import { SystemDataSequenceIds } from "building-types/dataSequences/systemDataSequenceTypes";
import { GRADE_DATUM_ID } from "building-types/datums/datumsTypes";
import { DOC_CACHE_SIZE, type Doc } from "building-types/doc";
import { BASE_PRESET_NAME } from "building-types/doc/docDefaultConstants";
import { BuiltinDoorId } from "building-types/doors/BuiltinDoorId";
import { LoadType } from "building-types/electrical/electricalTypesEnum";
import { BuiltInLabels } from "building-types/entities/labelTypes";
import { LumberProfileType, PlywoodProfile } from "building-types/framing/lumberTypes";
import { generatedLevelDatumNames } from "building-types/levels/levelTypes";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import {
  interiorBaseBoardMaterialChoices,
  interiorCeilingMaterialChoices,
  interiorCrownMoldingMaterialChoices,
  interiorDoorCasingMaterialChoices,
  interiorMaterialChoices,
  interiorWallMaterialChoices,
} from "building-types/settings/interiorMaterialSettings";
import { WallCondition } from "building-types/walls/wallType";
import { BuiltinWindowId } from "building-types/windows/windowId";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { defaultFoundationComponentEntityIds } from "doc/elements/foundations/defaultFoundationComponents";
import { builtInSheathingComponents, builtInSlabFloorComponents } from "../defaults/assemblyComponentDefaults";
import { defaultCaseworkTypes } from "../defaults/caseworkDefaults";
import { builtInFrameFloorAssemblies, builtInSlabFloorAssemblies } from "../defaults/defaultFloorAssembly";
import {
  builtInWallPlaneAssemblies,
  wallPlaneContentMutableBuiltInGroupIds,
} from "../defaults/defaultWallPlaneAssembly";
import { defaultPaletteIds } from "../defaults/paletteDefaults";
import { defaultDoorTypes } from "../elements/doors/doorCatalog";
import { builtInFoundationAssemblies } from "../elements/foundations/defaultFoundationAssemblies";
import { builtInHeaderAssemblies } from "../elements/framing/defaultHeaderAssembly";
import { BuiltInInteriorPropId } from "../elements/propCatalog/interiorPropCatalog";
import { defaultRailingSettingKey } from "../elements/railing/constants";
import { defaultStairSettingsKey } from "../elements/stairSystem/stairConstants";
import { defaultWindowTypes } from "../elements/window/windowCatalog";
import { isOptionSetId } from "../options/optionTools";
import { isSystemOptionSet } from "../options/systemOptionSet";
import { SystemOptionSet } from "../options/SystemOptionSetEnum";
import { SystemDrawingTemplates } from "../systemDrawingTemplates";
import { systemParametersFromDoc } from "../systemParameterTools";
import { defaultColumnsInternalIds } from "../utils/columnDescriptorUtils";
import { builtInAndLevelSpecificDatumIds } from "../utils/datumTools";
import { defaultWallMaterialIds } from "../utils/wallMaterialTools";
import { defaultWallTypes } from "../utils/wallTypeTools";

// Cache static system IDs that don't depend on doc content
const staticSystemIds: Set<string> = (() => {
  const ids = new Set<string>();

  // System datums
  for (const id of builtInAndLevelSpecificDatumIds) {
    ids.add(id);
  }

  // Shared grade datum used by resolved level datums (not a doc entity)
  ids.add(GRADE_DATUM_ID);

  // System palettes
  for (const id of defaultPaletteIds) {
    ids.add(id);
  }

  for (const id in ProfileExtrusionType) {
    if (id === ProfileExtrusionType.DEFAULT_PROFILE) {
      continue;
    }
    ids.add(id);
  }

  // System labels
  for (const id of BuiltInLabels) {
    ids.add(id);
  }

  // System casework catalog items
  for (const id in defaultCaseworkTypes) {
    ids.add(id);
  }

  // Base preset
  ids.add(BASE_PRESET_NAME);

  // System data sequences
  for (const id of SystemDataSequenceIds) {
    ids.add(id);
  }

  // System stair settings
  ids.add(defaultStairSettingsKey);

  // System railing settings
  ids.add(defaultRailingSettingKey);

  // System wall materials
  for (const id of defaultWallMaterialIds) {
    ids.add(id);
  }

  // System wall conditions
  for (const id in WallCondition) {
    ids.add(WallCondition[id]);
  }

  // System wall types
  for (const id in defaultWallTypes) {
    ids.add(id);
  }

  // System exterior props
  for (const id in BuiltInExteriorPropId) {
    ids.add(BuiltInExteriorPropId[id]);
  }

  // System interior props
  for (const id in BuiltInInteriorPropId) {
    ids.add(BuiltInInteriorPropId[id]);
  }

  // System foundation components
  for (const id of defaultFoundationComponentEntityIds) {
    ids.add(id);
  }

  // System foundation assemblies
  for (const id in builtInFoundationAssemblies) {
    ids.add(id);
  }

  // System column descriptors
  for (const id of Object.keys(defaultColumnsInternalIds)) {
    ids.add(id);
  }

  // System floor components
  for (const id of Object.keys(builtInSlabFloorComponents)) {
    ids.add(id);
  }

  for (const id of Object.keys(builtInSheathingComponents)) {
    ids.add(id);
  }

  // System floor assemblies
  for (const id of Object.keys(builtInFrameFloorAssemblies)) {
    ids.add(id);
  }

  for (const id of Object.keys(builtInSlabFloorAssemblies)) {
    ids.add(id);
  }

  for (const id of Object.keys(builtInHeaderAssemblies)) {
    ids.add(id);
  }

  // System wall plane assemblies
  for (const id of Object.keys(builtInWallPlaneAssemblies)) {
    ids.add(id);
  }

  // System wall plane content mutable built-in groups
  for (const id of wallPlaneContentMutableBuiltInGroupIds) {
    ids.add(id);
  }

  const systemInteriorMaterials = new Set([
    ...interiorMaterialChoices,
    ...interiorCeilingMaterialChoices,
    ...interiorWallMaterialChoices,
    ...interiorDoorCasingMaterialChoices,
    ...interiorBaseBoardMaterialChoices,
    ...interiorCrownMoldingMaterialChoices,
  ]);

  // System interior materials
  for (const id of systemInteriorMaterials) {
    ids.add(id);
  }

  // System option sets
  for (const id in SystemOptionSet) {
    ids.add(SystemOptionSet[id]);
  }

  // System door types
  for (const id in BuiltinDoorId) {
    ids.add(BuiltinDoorId[id]);
  }

  for (const id in defaultDoorTypes) {
    ids.add(id);
  }

  for (const id in PlywoodProfile) {
    ids.add(PlywoodProfile[id]);
  }

  for (const id in LumberProfileType) {
    ids.add(LumberProfileType[id]);
  }

  // System window types
  for (const id in BuiltinWindowId) {
    ids.add(BuiltinWindowId[id]);
  }

  for (const id in defaultWindowTypes) {
    ids.add(id);
  }

  // System electrical types
  for (const id in LoadType) {
    ids.add(LoadType[id]);
  }

  // System content templates
  for (const id in SystemDrawingTemplates) {
    ids.add(id);
  }

  return ids;
})();

// Cache results per doc since system parameters are doc-specific
const systemIdsCache = new LRUObjectCache<Doc, Set<string>>(DOC_CACHE_SIZE, (a, b) => a === b);

export function docCollectSystemIds(doc: Doc): Set<string> {
  const cached = systemIdsCache.get(doc);

  if (cached) {
    return cached;
  }

  // Start with static system IDs
  const systemIds = new Set(staticSystemIds);

  // Add doc-specific system parameters
  const { systemParameters } = systemParametersFromDoc(doc);
  const systemParameterIds = Object.keys(systemParameters);

  for (const id of systemParameterIds) {
    /**
     * System parameters are generated within systemParametersFromDoc default to whatever
     * option sets exist on the doc at the time of generation. This filter is meant to ensure
     * that "System" option set ids are truly that, in this context
     */
    if (isOptionSetId(id) && !isSystemOptionSet({ type: "OPTION_SET", id })) {
      continue;
    }

    systemIds.add(id);
  }

  // Resolved level-generated datums (e.g. `${levelId}-foundation`) are Absolute vertical
  // datum options but are not doc entities — accept them as system ids.
  for (const levelId of Object.keys(doc.levels)) {
    for (const name of generatedLevelDatumNames) {
      if (name === "grade") {
        continue;
      }
      systemIds.add(`${levelId}-${name}`);
    }
  }

  // Cache the result
  systemIdsCache.add(doc, systemIds);

  return systemIds;
}
